import { NullUUID, UUID } from "@bitory-gmbh/nidavellir";
import {
  updateVbsConfirmationCache,
  useVbsConfirmationShowQuery,
  useVbsConfirmationPatchMutation,
  useVbsConfirmationResetReferenceNumberMutation,
} from "../api/confirmation-api";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { Card, CardContent } from "@/shared/components/ui/card";
import { ConfirmationBaseForm } from "@/services/backend/documents/components/confirmation-base-form";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { ReferenceNumberField } from "@/services/backend/documents/components/reference-number-field";
import { FieldSeparator } from "@/shared/components/ui/field";
import { TextareaField } from "@/shared/components/field/textarea-field";
import { Confirmation } from "../types/confirmation";
import {
  ConfirmationBasePatchRequest,
  patchConfirmationBase,
} from "@/services/backend/documents/types/confirmation";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { DocumentState } from "@/services/backend/documents/types/state";

export function ConfirmationDataCard({
  confirmationId,
}: {
  confirmationId: UUID;
}) {
  const { data, error: dataError } = useVbsConfirmationShowQuery({
    id: confirmationId,
  });

  return (
    <div>
      {data && <ConfirmationData data={data} />}
      {dataError && <RTKQueryErrorAlert error={dataError} />}
    </div>
  );
}

function ConfirmationData({ data }: { data: Confirmation }) {
  const { id: confirmationId } = data;
  const [set] = useOptimisticDebouncedMutation(
    {
      id: data.id,
      date: data.date,
      orderDate: data.orderDate,
      orderReference: data.orderReference,
      offerDate: data.offerDate,
      offerReference: data.offerReference,
    },
    updateVbsConfirmationCache,
    useVbsConfirmationPatchMutation(),
  );
  const patchReferenceNumber = useVbsConfirmationPatchMutation();
  const resetReferenceNumber = useVbsConfirmationResetReferenceNumberMutation();

  return (
    <Card>
      <CardContent>
        <div className="grid gap-4">
          <ReferenceNumberField
            data={data}
            updateCache={updateVbsConfirmationCache}
            patchMutation={patchReferenceNumber}
            resetMutation={resetReferenceNumber}
            disabled={isImmutable(data)}
          />
          <ConfirmationBaseForm
            confirmationId={confirmationId}
            base={data}
            onBasePatch={(patch: ConfirmationBasePatchRequest) =>
              set({ id: data.id, ...patch }, patchConfirmationBase(data, patch))
            }
            cpyDestinationEntityName={CpyName.VBSConfirmation}
            cpyOrderSourceEntityName={CpyName.VBSOrder}
            cpyOfferSourceEntityName={CpyName.VBSOffer}
            cpyOfferSourcesParams={{
              serviceKind: { active: true, values: [GSMServiceKind.VBS] },
              process: {
                active: data.processId ? true : false,
                values: [data.processId ?? NullUUID],
              },
              state: { active: true, values: [DocumentState.Immutable] },
              orderBy: [{ field: "updatedAt" }],
            }}
            cpyOrderSourcesParams={{
              serviceKind: { active: true, values: [GSMServiceKind.VBS] },
              process: {
                active: data.processId ? true : false,
                values: [data.processId ?? NullUUID],
              },
              orderBy: [{ field: "updatedAt" }],
            }}
            disabled={isImmutable(data)}
          />
          <FieldSeparator />
          <TextareaField
            label={t("Interner Kommentar")}
            value={data.internalComment}
            onChange={(e) =>
              set(
                { id: data.id, internalComment: e.target.value },
                { ...data, internalComment: e.target.value },
              )
            }
            fieldName="internalComment"
            disabled={isImmutable(data)}
          />
        </div>
      </CardContent>
    </Card>
  );
}
